import type React from "react";
import { Check, Clock, X } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { ITimesheet } from "../types/timesheet.api.types";
import type { WeekOffConfig } from "./weekOff";
import { isWeekOffDate } from "./weekOff";
import {
  isApprovedWorkflowStatus,
  isPendingWorkflowStatus,
  isRejectedWorkflowStatus,
} from "./constants";
import { projectColor } from "../constants/timesheet";

dayjs.extend(utc);
dayjs.extend(timezone);

/* =========================================================
   Week range (Sunday -> Saturday, local calendar dates)
========================================================= */

export interface WeekRange {
  start: Date;
  end: Date;
  days: Date[];
}

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function getWeekRange(offset: number): WeekRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sinceSunday = (today.getDay() + 7) % 7;
  const sunday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - sinceSunday,
  );
  const start = new Date(
    sunday.getFullYear(),
    sunday.getMonth(),
    sunday.getDate() + offset * 7,
  );
  const days = Array.from(
    { length: 7 },
    (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
  const end = days[6];
  return { start, end, days };
}

export function toWeekDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Timesheet dates are stored as start-of-day UTC of the local calendar date
 * (e.g. 2026-08-09T00:00:00.000Z for "9 Aug"). Reconstruct the calendar
 * date key from the UTC components so entries map onto the correct local day.
 */
export function toEntryDateKey(dateString: string): string {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatWeekSubtitle(range: WeekRange): string {
  const s = range.start;
  const e = range.end;
  const startDay = String(s.getDate()).padStart(2, "0");
  const endDay = String(e.getDate()).padStart(2, "0");
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `Week of ${startDay} – ${endDay} ${MONTH_LABELS[e.getMonth()]} ${e.getFullYear()}`;
  }
  return `Week of ${startDay} ${MONTH_LABELS[s.getMonth()]} – ${endDay} ${MONTH_LABELS[e.getMonth()]} ${e.getFullYear()}`;
}

export function formatWeekRangeLabel(range: WeekRange): string {
  const s = range.start;
  const e = range.end;
  const startDay = String(s.getDate()).padStart(2, "0");
  const endDay = String(e.getDate()).padStart(2, "0");
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `Week ${startDay} – ${endDay} ${MONTH_LABELS[e.getMonth()]} ${e.getFullYear()}`;
  }
  return `Week ${startDay} ${MONTH_LABELS[s.getMonth()]} – ${endDay} ${MONTH_LABELS[e.getMonth()]} ${e.getFullYear()}`;
}

export interface WeekOption {
  offset: number;
  startKey: string;
  endKey: string;
  label: string;
}

export function getRecentWeekOptions(count = 12): WeekOption[] {
  return Array.from({ length: count }, (_, i) => {
    const offset = 0 - i;
    const range = getWeekRange(offset);
    return {
      offset,
      startKey: toWeekDateKey(range.start),
      endKey: toWeekDateKey(range.end),
      label: formatWeekRangeLabel(range),
    };
  });
}

export function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/* =========================================================
   Formatting
========================================================= */

export function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "0h";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/* =========================================================
   Week target (multi-tenant working hours x working days)
========================================================= */

export function countWorkingDays(
  days: Date[],
  weekOffConfig?: WeekOffConfig | null,
): number {
  return days.filter((d) => !isWeekOffDate(d, weekOffConfig)).length;
}

export function getRequiredDayHours(
  date: Date,
  weekOffConfig?: WeekOffConfig | null,
  fullDayHours = 7,
): number {
  if (isWeekOffDate(date, weekOffConfig)) return 0;
  return Number(fullDayHours) || 7;
}

/* =========================================================
   Status pill (matches Crewknit My Timesheets design)
========================================================= */

export interface DayStatusPill {
  label: string;
  bg: string;
  text: string;
  /** Full dynamic workflow status label (tooltip detail), when available. */
  detail?: string;
}

const PILL_STYLES: Record<string, { bg: string; text: string }> = {
  draft: {
    bg: "var(--color-bg-muted)",
    text: "var(--color-text-secondary)",
  },
  submitted: {
    bg: "var(--color-info-light)",
    text: "var(--color-info)",
  },
  approved: {
    bg: "var(--color-success-light)",
    text: "var(--color-success-dark)",
  },
  rejected: {
    bg: "var(--color-error-light)",
    text: "var(--color-error-dark)",
  },
};

export function getDayStatusPill(
  status?: string,
  statusLabel?: string,
): DayStatusPill {
  const s = String(status || "").toLowerCase();
  if (!s) {
    return { label: "No tasks yet", ...PILL_STYLES.draft };
  }
  if (s === "draft") {
    return { label: "Draft", detail: statusLabel, ...PILL_STYLES.draft };
  }
  if (isApprovedWorkflowStatus(s)) {
    return { label: "Approved", detail: statusLabel, ...PILL_STYLES.approved };
  }
  if (isRejectedWorkflowStatus(s)) {
    return { label: "Rejected", detail: statusLabel, ...PILL_STYLES.rejected };
  }
  if (isPendingWorkflowStatus(s)) {
    return { label: "Pending", detail: statusLabel, ...PILL_STYLES.submitted };
  }
  return { label: statusLabel || s, detail: statusLabel, ...PILL_STYLES.draft };
}

export interface TimesheetStatusPill {
  label: string;
  bg: string;
  text: string;
  Icon: React.ElementType;
}

export function getTimesheetStatusPill(record: {
  status?: string;
  statusLabel?: string;
}): TimesheetStatusPill {
  const status = String(record.status || "").toLowerCase();
  if (isApprovedWorkflowStatus(status)) {
    return {
      label: record.statusLabel || "Approved",
      bg: "var(--color-success-light)",
      text: "var(--color-success-dark)",
      Icon: Check,
    };
  }
  if (isRejectedWorkflowStatus(status)) {
    return {
      label: record.statusLabel || "Rejected",
      bg: "var(--color-error-light)",
      text: "var(--color-error-dark)",
      Icon: X,
    };
  }
  if (isPendingWorkflowStatus(status) || status.startsWith("pending_")) {
    return {
      label: record.statusLabel || "Submitted",
      bg: "var(--color-info-light)",
      text: "var(--color-info)",
      Icon: Clock,
    };
  }
  return {
    label: record.statusLabel || "Draft",
    bg: "var(--color-bg-muted)",
    text: "var(--color-text-secondary)",
    Icon: Clock,
  };
}

export function getUtilColor(pct: number): string {
  if (pct >= 100) return "var(--color-success-dark)";
  if (pct >= 85) return "var(--color-info)";
  return "#B45309";
}


/* =========================================================
   Project aggregation (segmented bar + legend)
========================================================= */

export interface ProjectAggregate {
  name: string;
  hours: number;
  color: string;
  widthPct: number;
}

interface TaskHours {
  project?: string;
  hours: number;
}

function aggregateTasks(tasks: TaskHours[]): ProjectAggregate[] {
  const map = new Map<string, number>();
  for (const t of tasks) {
    const name = t.project?.trim() || "Unassigned";
    map.set(name, (map.get(name) || 0) + (t.hours || 0));
  }
  const total = [...map.values()].reduce((a, b) => a + b, 0) || 0;
  return [...map.entries()]
    .map(([name, hours]) => ({
      name,
      hours,
      color: projectColor(name),
      widthPct: total > 0 ? Math.round((hours / total) * 100) : 0,
    }))
    .sort((a, b) => b.hours - a.hours);
}

export function aggregateProjectHours(entries: ITimesheet[]): ProjectAggregate[] {
  const tasks: TaskHours[] = [];
  for (const e of entries) {
    for (const t of e.tasks) tasks.push({ project: t.project, hours: t.hours });
  }
  return aggregateTasks(tasks);
}

/* =========================================================
   Draft editor helpers
========================================================= */

export interface DraftTask {
  key: string;
  name: string;
  project: string;
  startTime: string; // "HH:mm"
  endTime: string;   // "HH:mm"
  description: string;
}

export function createDraftTask(): DraftTask {
  return { key: Math.random().toString(36).slice(2, 10), name: "", project: "", startTime: "", endTime: "", description: "" };
}

export function taskToDraft(
  task: { name: string; project?: string; startTime: string; endTime: string; description?: string },
  timezone: string,
): DraftTask {
  return {
    key: Math.random().toString(36).slice(2, 10),
    name: task.name,
    project: task.project || "",
    startTime: dayjs(task.startTime).tz(timezone).format("HH:mm"),
    endTime: dayjs(task.endTime).tz(timezone).format("HH:mm"),
    description: task.description || "",
  };
}

function timeToMinutes(time: string): number {
  const [h, m] = String(time).split(":").map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return NaN;
  return h * 60 + m;
}

export function computeTaskHours(task: DraftTask): number {
  if (!task.startTime || !task.endTime) return 0;
  const start = timeToMinutes(task.startTime);
  const end = timeToMinutes(task.endTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  const minutes = (end - start + 1440) % 1440;
  if (minutes === 0) return 0;
  return Number((minutes / 60).toFixed(2));
}

export function isTaskEmpty(task: DraftTask): boolean {
  return (
    !task.name.trim() &&
    !task.project.trim() &&
    !task.startTime &&
    !task.endTime
  );
}

export function isTaskValid(task: DraftTask): boolean {
  if (!task.name.trim() || !task.startTime || !task.endTime) return false;
  const start = timeToMinutes(task.startTime);
  const end = timeToMinutes(task.endTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return start !== end;
}

export function isDayFilled(tasks: DraftTask[]): boolean {
  return tasks.some((t) => !isTaskEmpty(t) && isTaskValid(t));
}

export function dayFilledHours(tasks: DraftTask[]): number {
  return tasks
    .filter((t) => !isTaskEmpty(t) && isTaskValid(t))
    .reduce((sum, t) => sum + computeTaskHours(t), 0);
}

/* =========================================================
   Week totals across editable + locked days
========================================================= */

export interface DayEntryMap {
  [dateKey: string]: ITimesheet | undefined;
}

export function computeWeekHours(
  days: Date[],
  editorTasks: Record<string, DraftTask[]>,
  entryByDate: DayEntryMap,
): number {
  let total = 0;
  for (const day of days) {
    const key = toWeekDateKey(day);
    const entry = entryByDate[key];
    if (entry && entry.status !== "draft") {
      total += entry.totalHours || 0;
      continue;
    }
    total += dayFilledHours(editorTasks[key] || []);
  }
  return Number(total.toFixed(2));
}

export function computeWeekProjects(
  days: Date[],
  editorTasks: Record<string, DraftTask[]>,
  entryByDate: DayEntryMap,
): ProjectAggregate[] {
  const tasks: TaskHours[] = [];
  for (const day of days) {
    const key = toWeekDateKey(day);
    const entry = entryByDate[key];
    if (entry && entry.status !== "draft") {
      for (const t of entry.tasks) tasks.push({ project: t.project, hours: t.hours });
    } else {
      for (const t of editorTasks[key] || []) {
        if (isTaskEmpty(t) || !isTaskValid(t)) continue;
        tasks.push({ project: t.project, hours: computeTaskHours(t) });
      }
    }
  }
  return aggregateTasks(tasks);
}

/* =========================================================
   Build submit-week payload
========================================================= */

export interface SubmitWeekBuild {
  entries: {
    date: string;
    tasks: {
      name: string;
      project?: string;
      startTime: string;
      endTime: string;
      hours: number;
      description?: string;
    }[];
    totalHours: number;
  }[];
  invalidDays: { key: string; label: string }[];
  filledCount: number;
}

export function toSubmitWeekEntries(
  days: Date[],
  editorTasks: Record<string, DraftTask[]>,
  timezone: string,
): SubmitWeekBuild {
  const entries: SubmitWeekBuild["entries"] = [];
  const invalidDays: SubmitWeekBuild["invalidDays"] = [];
  let filledCount = 0;

  for (const day of days) {
    const key = toWeekDateKey(day);
    const tasks = editorTasks[key] || [];
    const filled = tasks.filter((t) => !isTaskEmpty(t));
    if (filled.length === 0) continue;
    filledCount += 1;

    if (filled.some((t) => !isTaskValid(t))) {
      invalidDays.push({ key, label: DAY_LABELS[day.getDay()] });
      continue;
    }

    const payloadTasks = filled.map((t) => ({
      name: t.name.trim(),
      project: t.project.trim() || undefined,
      startTime: dayjs(`${key} ${t.startTime}`).tz(timezone).utc().toISOString(),
      endTime: dayjs(`${key} ${t.endTime}`).tz(timezone).utc().toISOString(),
      hours: computeTaskHours(t),
      description: t.description.trim() || undefined,
    }));
    const totalHours = Number(
      payloadTasks.reduce((sum, x) => sum + x.hours, 0).toFixed(2),
    );
    entries.push({ date: key, tasks: payloadTasks, totalHours });
  }

  return { entries, invalidDays, filledCount };
}

/* =========================================================
   Week submission validation (pure business logic)
========================================================= */

export interface WeekSubmissionValidation {
  isValid: boolean;
  missingOrIncompleteDays: string[];
  timesheetIdsToSubmit: string[];
  errorMessage?: string;
}

export function validateWeekSubmission(
  days: Date[],
  entryByDate: DayEntryMap,
  weekOffConfig: WeekOffConfig | null | undefined,
  fullDayHours: number,
): WeekSubmissionValidation {
  const missingOrIncompleteDays: string[] = [];

  for (const day of days) {
    const isWeekOff = isWeekOffDate(day, weekOffConfig);
    if (isWeekOff) continue; // Week-off days are optional

    const key = toWeekDateKey(day);
    const entry = entryByDate[key];
    const dayLabel = DAY_LABELS[day.getDay()];

    // If already submitted/pending/approved/rejected, it's already in workflow -> OK!
    if (entry && entry.status !== "draft") continue;

    // If draft exists with totalHours >= fullDayHours -> OK!
    if (entry && entry.status === "draft" && (entry.totalHours ?? 0) >= fullDayHours) {
      continue;
    }

    // Otherwise, this working day is missing or incomplete
    missingOrIncompleteDays.push(`${dayLabel} (${key})`);
  }

  if (missingOrIncompleteDays.length > 0) {
    return {
      isValid: false,
      missingOrIncompleteDays,
      timesheetIdsToSubmit: [],
      errorMessage: `Cannot submit week. Please fill out all remaining working days: ${missingOrIncompleteDays.join(", ")}`,
    };
  }

  // Collect all remaining draft timesheet IDs across working days + filled weekend days
  const timesheetIdsToSubmit: string[] = [];
  for (const day of days) {
    const key = toWeekDateKey(day);
    const entry = entryByDate[key];
    if (entry && entry.status === "draft" && entry.id) {
      timesheetIdsToSubmit.push(entry.id);
    }
  }

  if (timesheetIdsToSubmit.length === 0) {
    return {
      isValid: false,
      missingOrIncompleteDays: [],
      timesheetIdsToSubmit: [],
      errorMessage: "No pending draft timesheets to submit for this week",
    };
  }

  return {
    isValid: true,
    missingOrIncompleteDays: [],
    timesheetIdsToSubmit,
  };
}

